/**
 * ValidationStepsPage.tsx — Step-by-step validation of the full design chain
 *
 * Walks through every literature-band check in the tool, live against the
 * active session. Each step is one row: parameter, computed value, target
 * band, source reference, and a clear PASS / WARN / FAIL badge.
 *
 * Grouped by chain level: Pore → Bubble → Module → Array → Reservoir →
 * Sediment → Stratification → Ecosystem → Compliance.
 *
 * Designed so a technical reviewer can scan it like a punch-list — the
 * counts at the top show how many checks pass and how many need attention.
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  ClipboardCheck, ArrowLeft, RefreshCcw, CheckCircle2, AlertTriangle,
  XCircle, Filter, Sliders,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  poresPerUnit, poresPerModule, qPerModuleNm3h,
  bubbleDiameterMm, bubbleRiseVelMs, sotePerMetre,
  surfaceRadiusM, moduleBedCoverageAreaM2,
  moduleSOTRkgh, moduleAOTRkgh,
} from '@/lib/diffuserPhysics';
import AwwaM37Toggle, { useAwwaOverride } from '@/components/AwwaM37Toggle';
import { useModulesEnabled } from '@/lib/moduleRegistry';

const G = 9.81;
const RESERVOIR_AREA_M2 = 34_000;
const RESERVOIR_AREA_HA = 3.4;

interface ModSpec extends LegacyModSpec {
  waterDepthM: number; mountingHeightM: number; waterTempC: number;
  currentDoMgL?: number; alphaFactor?: number; betaFactor?: number;
  sodKgM2D?: number;
}

const DEFAULTS: ModSpec = {
  diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12,
  currentDoMgL: 2.0, alphaFactor: 0.85, betaFactor: 0.98, sodKgM2D: 0.6,
};

const ARRAY_KEY = 'rb_module_array_v1';
interface ArrayCfg { moduleCount: number; cols: number; gapXM: number; gapYM: number }
const ARRAY_DEFAULTS: ArrayCfg = { moduleCount: 6, cols: 2, gapXM: 2.0, gapYM: 2.0 };

type Status = 'pass' | 'warn' | 'fail';

interface ValidationStep {
  id: string;
  level: string;
  step: string;
  param: string;
  value: number | string;
  unit: string;
  target: string;
  ref: string;
  status: Status;
  detail: string;
}

function fmt(n: number, d = 2) {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

function classify(value: number, ok: { min?: number; max?: number; warnMin?: number; warnMax?: number }): Status {
  const { min, max, warnMin, warnMax } = ok;
  const inFail = (min !== undefined && value < min) || (max !== undefined && value > max);
  if (inFail) return 'fail';
  const inWarn = (warnMin !== undefined && value < warnMin) || (warnMax !== undefined && value > warnMax);
  if (inWarn) return 'warn';
  return 'pass';
}

const LEVEL_COLOURS: Record<string, string> = {
  'L1 Pore':         'border-blue-700/50 bg-blue-900/10',
  'L2 Bubble':       'border-sky-700/50 bg-sky-900/10',
  'L3 Module':       'border-teal-700/50 bg-teal-900/10',
  'L4 Array':        'border-emerald-700/50 bg-emerald-900/10',
  'L5 Reservoir':    'border-lime-700/50 bg-lime-900/10',
  'L6 Sediment':     'border-amber-700/50 bg-amber-900/10',
  'L7 Stratification': 'border-orange-700/50 bg-orange-900/10',
  'L8 Ecosystem':    'border-violet-700/50 bg-violet-900/10',
  'L9 Compliance':   'border-fuchsia-700/50 bg-fuchsia-900/10',
};

export default function ValidationStepsPage() {
  const navigate = useNavigate();
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) }));
  const [array, setArray] = useState<ArrayCfg>(() => ({ ...ARRAY_DEFAULTS, ...loadPersisted<ArrayCfg>(ARRAY_KEY, ARRAY_DEFAULTS) }));
  const [filter, setFilter] = useState<'all' | Status>('all');

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) });
      if (k === ARRAY_KEY) setArray({ ...ARRAY_DEFAULTS, ...loadPersisted<ArrayCfg>(ARRAY_KEY, ARRAY_DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const { enabled: awwaOverride } = useAwwaOverride();
  const { isEnabled: modOn } = useModulesEnabled();

  const steps = useMemo<ValidationStep[]>(() => {
    const dSpec = specFromLegacy(spec);
    const T = spec.waterTempC;
    const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);
    const V_res = RESERVOIR_AREA_M2 * H_eff;

    const poresEach = poresPerUnit(dSpec);
    const poresMod = poresPerModule(dSpec);
    const qMod = qPerModuleNm3h(dSpec);
    const q_pore = qMod / Math.max(1, poresMod);
    const d_b = bubbleDiameterMm(dSpec, T);
    const v_rise = bubbleRiseVelMs(d_b, T);
    const sote_m = sotePerMetre(d_b);
    const r_surf = surfaceRadiusM(dSpec, H_eff);
    const A_mod = moduleBedCoverageAreaM2(dSpec, H_eff);
    const SOTR_mod = moduleSOTRkgh(dSpec, H_eff, T);
    const AOTR_mod = moduleAOTRkgh(dSpec, H_eff, T, spec.alphaFactor ?? 0.85, spec.betaFactor ?? 0.98, spec.currentDoMgL ?? 2.0);
    const AOTR_total = AOTR_mod * array.moduleCount;
    const SOD_kgH = (spec.sodKgM2D ?? 0.6) * RESERVOIR_AREA_M2 / 24;
    const ratio_AOTR_SOD = AOTR_total / Math.max(0.001, SOD_kgH);
    const Q_m3s = (array.moduleCount * qMod) / 3600;
    const eps_aer = (Q_m3s * G * H_eff) / Math.max(1, V_res);
    const awwa_min = Math.ceil(1 * RESERVOIR_AREA_HA);
    const awwa_max = Math.ceil(6 * RESERVOIR_AREA_HA);
    const cov_pct = (array.moduleCount * A_mod * 0.85 / RESERVOIR_AREA_M2) * 100;

    // Eo (Eötvös) — bubble shape
    const rho_w = 1000;
    const sigma_w = 0.073;
    const d_b_m = d_b / 1000;
    const Eo = (rho_w - 1.2) * G * d_b_m * d_b_m / sigma_w;

    // Reynolds bubble
    const nu_w = 1.31e-6;
    const Re_b = v_rise * d_b_m / nu_w;

    // Froude plume
    const B0_mod = G * (qMod / 3600);
    const Fr_plume = Math.pow(B0_mod / (G * H_eff), 0.5) / Math.max(0.1, H_eff);

    // Shields — needs sediment properties; placeholder
    const d50 = 50e-6;        // 50 µm silt
    const rho_s = 2600;
    const U_plume = 1.46 * Math.pow(B0_mod * H_eff, 1/3);
    const tau = rho_w * 0.01 * U_plume * U_plume;
    const theta = tau / Math.max(1e-9, (rho_s - rho_w) * G * d50);
    const theta_cr = 0.10 * 2.5;     // fine silt × cohesion

    return [
      // L1 — Pore
      { id: 's1.1', level: 'L1 Pore', step: '1.1', param: 'Pores per diffuser', value: poresEach, unit: '–', target: '≥ 100', ref: 'EPA 1989', status: classify(poresEach, { min: 100, warnMin: 200 }), detail: 'Total pore count drives bubble size and pressure-drop' },
      { id: 's1.2', level: 'L1 Pore', step: '1.2', param: 'Air per pore', value: fmt(q_pore * 1e6, 2), unit: 'mL/s', target: '0.05–1.0', ref: 'Sanitaire', status: classify(q_pore * 1e6, { min: 0.05, max: 1.0, warnMin: 0.10, warnMax: 0.7 }), detail: 'Too low: clogging; too high: jetting (Iguchi 1998)' },

      // L2 — Bubble
      { id: 's2.1', level: 'L2 Bubble', step: '2.1', param: 'Bubble Ø (Fritz + Loubière)', value: fmt(d_b, 2), unit: 'mm', target: '1.0–5.0 mm', ref: 'Fritz 1935', status: classify(d_b, { min: 1.0, max: 5.0, warnMin: 1.5, warnMax: 4.0 }), detail: 'Fine-bubble regime for high SOTE' },
      { id: 's2.2', level: 'L2 Bubble', step: '2.2', param: 'Rise velocity', value: fmt(v_rise, 3), unit: 'm/s', target: '0.10–0.40', ref: 'Clift 1978', status: classify(v_rise, { min: 0.10, max: 0.40 }), detail: 'Within ellipsoidal-bubble regime' },
      { id: 's2.3', level: 'L2 Bubble', step: '2.3', param: 'Bubble Reynolds Re_b', value: fmt(Re_b, 0), unit: '–', target: '100–2000', ref: 'Clift 1978', status: classify(Re_b, { min: 100, max: 2000 }), detail: 'Ellipsoidal regime' },
      { id: 's2.4', level: 'L2 Bubble', step: '2.4', param: 'Eötvös number', value: fmt(Eo, 2), unit: '–', target: '< 40', ref: 'Clift 1978', status: classify(Eo, { max: 40, warnMax: 20 }), detail: 'Below spherical-cap transition' },
      { id: 's2.5', level: 'L2 Bubble', step: '2.5', param: 'SOTE per metre', value: fmt(sote_m, 2), unit: '%', target: '≥ 2.5 %/m', ref: 'Stenstrom & Rosso 2008', status: classify(sote_m, { min: 2.5, warnMin: 3.5 }), detail: 'Oxygen transfer efficiency' },

      // L3 — Module
      { id: 's3.1', level: 'L3 Module', step: '3.1', param: 'Per-module airflow', value: fmt(qMod, 1), unit: 'Nm³/h', target: '> 1', ref: 'design', status: classify(qMod, { min: 1, warnMin: 3 }), detail: 'Sufficient gas-flow per module' },
      { id: 's3.2', level: 'L3 Module', step: '3.2', param: 'Module SOTR', value: fmt(SOTR_mod, 3), unit: 'kg/h', target: '> 0.02', ref: 'ASCE 2-06', status: classify(SOTR_mod, { min: 0.02, warnMin: 0.05 }), detail: 'Clean-water oxygen transfer rate' },
      { id: 's3.3', level: 'L3 Module', step: '3.3', param: 'Module AOTR', value: fmt(AOTR_mod, 3), unit: 'kg/h', target: '> 0.005', ref: 'Metcalf & Eddy 2014', status: classify(AOTR_mod, { min: 0.005 }), detail: 'α-β-T corrected, at current DO' },
      { id: 's3.4', level: 'L3 Module', step: '3.4', param: 'Module bed coverage', value: fmt(A_mod, 0), unit: 'm²', target: '> 20 m²', ref: 'Wüest 1992', status: classify(A_mod, { min: 20, warnMin: 50 }), detail: 'Per-module footprint of active aeration' },
      { id: 's3.5', level: 'L3 Module', step: '3.5', param: 'Surface plume radius', value: fmt(r_surf, 2), unit: 'm', target: '> 1.0 m', ref: 'Seol 2007', status: classify(r_surf, { min: 1.0, warnMin: 2.0 }), detail: 'Surface boil footprint per diffuser' },

      // L4 — Array
      { id: 's4.1', level: 'L4 Array', step: '4.1', param: 'Module count designed', value: array.moduleCount, unit: '–', target: awwaOverride ? 'no cap (override)' : `${awwa_min}–${awwa_max}`, ref: awwaOverride ? 'AWWA M37 (BYPASSED)' : 'AWWA M37', status: awwaOverride ? 'pass' : classify(array.moduleCount, { min: awwa_min, max: awwa_max }), detail: awwaOverride ? 'AWWA M37 cap bypassed — module count not constrained' : 'Within AWWA M37 density envelope' },
      { id: 's4.2', level: 'L4 Array', step: '4.2', param: 'Total airflow', value: fmt(array.moduleCount * qMod, 0), unit: 'Nm³/h', target: 'sized', ref: 'aggregate', status: 'pass', detail: 'N × q_module' },
      { id: 's4.3', level: 'L4 Array', step: '4.3', param: 'Total AOTR', value: fmt(AOTR_total, 2), unit: 'kg/h', target: '> SOD', ref: 'mass balance', status: classify(AOTR_total - SOD_kgH, { min: 0, warnMin: 0.1 }), detail: 'Must exceed sediment oxygen demand' },
      { id: 's4.4', level: 'L4 Array', step: '4.4', param: 'Bed coverage (with overlap)', value: fmt(cov_pct, 1), unit: '%', target: '> 95 %', ref: 'continuous mixing', status: classify(cov_pct, { min: 95, warnMin: 80 }), detail: 'Percentage of bed area within active aeration footprint' },

      // L5 — Reservoir
      { id: 's5.1', level: 'L5 Reservoir', step: '5.1', param: 'AOTR / SOD ratio', value: fmt(ratio_AOTR_SOD, 2), unit: '×', target: '≥ 1.2', ref: 'design margin', status: classify(ratio_AOTR_SOD, { min: 1.2, warnMin: 1.5 }), detail: '20 % margin above demand' },
      { id: 's5.2', level: 'L5 Reservoir', step: '5.2', param: 'Aeration ε (Wüest)', value: eps_aer.toExponential(2), unit: 'W/kg', target: '> 5×10⁻⁷', ref: 'Wüest 1992', status: classify(eps_aer, { min: 5e-7 }), detail: 'Turbulent dissipation rate from gas injection' },
      { id: 's5.3', level: 'L5 Reservoir', step: '5.3', param: 'Visser cyano suppression', value: eps_aer > 5e-7 ? 'YES' : 'NO', unit: '', target: 'YES', ref: 'Visser 1996', status: eps_aer > 5e-7 ? 'pass' : 'fail', detail: 'Cyanobacteria lose buoyancy advantage above critical ε' },

      // L6 — Sediment
      { id: 's6.1', level: 'L6 Sediment', step: '6.1', param: 'Shields θ', value: fmt(theta, 3), unit: '–', target: `< 2 × θ_cr (${fmt(2 * theta_cr, 3)})`, ref: 'Shields 1936', status: classify(theta, { max: 2 * theta_cr, warnMax: theta_cr }), detail: 'Below resuspension threshold' },
      { id: 's6.2', level: 'L6 Sediment', step: '6.2', param: 'Bed velocity', value: fmt(U_plume, 3), unit: 'm/s', target: '< 0.15', ref: 'Seol 2007', status: classify(U_plume, { max: 0.15, warnMax: 0.08 }), detail: 'Plume-induced velocity at bed' },
      { id: 's6.3', level: 'L6 Sediment', step: '6.3', param: 'Aerobic depth', value: '> 0.5 mm', unit: '', target: 'aerobic interface', ref: 'Rasmussen 1992', status: eps_aer > 5e-7 ? 'pass' : 'warn', detail: 'O₂ penetration into sediment surface' },
      // ── Module: Resuspension penalty (when enabled) ───────────────────────
      ...(modOn('resusp_penalty') ? (() => {
        // Conservative τ_uncover for medium silt (default bed type)
        const tau_uncover = 0.3;
        const ratio = tau / tau_uncover;
        const willUncover = ratio > 1;
        return [{
          id: 's6.4', level: 'L6 Sediment', step: '6.4',
          param: 'Resuspension penalty (organic uncovering)', value: fmt(ratio, 2), unit: '× τ_uncover',
          target: '< 1.0 (no organic uncovering)', ref: 'Bryant et al. 2011 [MODULE]',
          status: willUncover ? 'fail' as Status : 'pass' as Status,
          detail: willUncover
            ? `Plume bed-shear (${fmt(tau, 3)} Pa) exceeds organic-uncovering threshold; expect 20–30% SOD uplift`
            : 'Plume shear below organic-uncovering threshold; SOD remains at design value',
        }];
      })() : []),

      // L7 — Stratification
      { id: 's7.1', level: 'L7 Stratification', step: '7.1', param: 'Plume Froude Fr_plume', value: fmt(Fr_plume, 2), unit: '–', target: '> 0.3', ref: 'Imberger 1985', status: classify(Fr_plume, { min: 0.3 }), detail: 'Buoyancy-dominated regime' },
      { id: 's7.2', level: 'L7 Stratification', step: '7.2', param: 'Mixing fasterthan growth', value: eps_aer > 1e-6 ? 'YES' : 'NO', unit: '', target: 'YES', ref: 'Reynolds 1984', status: eps_aer > 1e-6 ? 'pass' : eps_aer > 5e-7 ? 'warn' : 'fail', detail: 'Mixing rate ε > algal doubling rate' },
      // ── Module: Asaeda-Imberger C-criterion (when enabled) ────────────────
      ...(modOn('asaeda_c') ? (() => {
        const dRho = 0.18 * 5.0;  // assume summer stratification ΔT = 5 °C
        const N = Math.sqrt(Math.max(0, (G / 1000) * (dRho / Math.max(0.1, spec.waterDepthM))));
        const B_0 = G * (qMod / 3600);
        const C_W = N * Math.pow(spec.waterDepthM, 0.75) / Math.pow(Math.max(1e-9, B_0), 0.25);
        const verdict = C_W < 1.4 ? 'fail' : C_W < 2.4 ? 'warn' : 'pass';
        return [{
          id: 's7.3', level: 'L7 Stratification', step: '7.3',
          param: 'Asaeda-Imberger C-criterion', value: fmt(C_W, 2), unit: '–',
          target: '> 2.4 (Type-1 independent)', ref: 'Asaeda & Imberger 1993 [MODULE]',
          status: verdict as Status,
          detail: 'Adjacent-plume merging threshold under stratification. C < 1.4 = merged (loss of efficiency)',
        }];
      })() : []),

      // L8 — Ecosystem
      { id: 's8.1', level: 'L8 Ecosystem', step: '8.1', param: 'Phosphate release', value: '< 5', unit: 'mg/m²/d', target: 'aerobic', ref: 'Welch & Cooke 1995', status: eps_aer > 5e-7 ? 'pass' : 'warn', detail: 'Internal P loading suppression via Fe(III)-P binding' },
      { id: 's8.2', level: 'L8 Ecosystem', step: '8.2', param: 'Algal bloom suppression', value: eps_aer > 1e-6 ? 'YES' : 'NO', unit: '', target: 'YES', ref: 'Reynolds 1984 / Heisler 2008', status: eps_aer > 1e-6 ? 'pass' : 'warn', detail: 'Mixing energy exceeds algal-growth threshold' },

      // L9 — Compliance
      { id: 's9.1', level: 'L9 Compliance', step: '9.1', param: 'AWWA M37 envelope', value: awwaOverride ? 'BYPASSED' : String(array.moduleCount), unit: awwaOverride ? '' : 'modules', target: awwaOverride ? 'override active' : `${awwa_min}–${awwa_max}`, ref: 'AWWA M37 (2011)', status: awwaOverride ? 'pass' : classify(array.moduleCount, { min: awwa_min, max: awwa_max }), detail: awwaOverride ? 'AWWA M37 (1–6 modules/ha) cap intentionally bypassed. Documented engineering decision required.' : 'Module density 1–6 per hectare' },
      { id: 's9.2', level: 'L9 Compliance', step: '9.2', param: 'DO target (drinking water source)', value: fmt(spec.currentDoMgL ?? 0, 1), unit: 'mg/L', target: '≥ 6 mg/L', ref: 'DWI / WHO', status: classify(spec.currentDoMgL ?? 0, { min: 6, warnMin: 4 }), detail: 'DO at draw-off after aeration steady state' },
    ];
  }, [spec, array, awwaOverride, modOn]);

  const counts = useMemo(() => ({
    pass: steps.filter(s => s.status === 'pass').length,
    warn: steps.filter(s => s.status === 'warn').length,
    fail: steps.filter(s => s.status === 'fail').length,
  }), [steps]);

  const filtered = filter === 'all' ? steps : steps.filter(s => s.status === filter);

  const groups = useMemo(() => {
    const out = new Map<string, ValidationStep[]>();
    for (const s of filtered) {
      if (!out.has(s.level)) out.set(s.level, []);
      out.get(s.level)!.push(s);
    }
    return out;
  }, [filtered]);

  const overallScore = Math.round((counts.pass / Math.max(1, steps.length)) * 100);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={ClipboardCheck}
        title="Validation Steps — Step-by-Step Live Check"
        subtitle={`Every literature-band check across the design chain. ${counts.pass} pass · ${counts.warn} warn · ${counts.fail} fail`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <AwwaM37Toggle compact />
            <button onClick={() => setSpec({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) })}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700">
              <RefreshCcw className="w-3.5 h-3.5" /> Reload
            </button>
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border-2 border-rb-brand/40 bg-rb-brand/10 p-4">
          <div className="text-xs uppercase tracking-wider text-rb-brand">Overall pass rate</div>
          <div className="text-3xl font-bold text-slate-100 mt-1">{overallScore} %</div>
          <div className="text-xs text-slate-400 mt-1">{counts.pass} of {steps.length} checks</div>
        </div>
        <button onClick={() => setFilter(filter === 'pass' ? 'all' : 'pass')}
          className={`text-left rounded-xl border-2 p-4 transition ${filter === 'pass' ? 'border-emerald-500 bg-emerald-900/30' : 'border-emerald-700 bg-emerald-900/15 hover:bg-emerald-900/25'}`}>
          <div className="text-xs uppercase tracking-wider text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Pass</div>
          <div className="text-3xl font-bold text-emerald-200 mt-1">{counts.pass}</div>
        </button>
        <button onClick={() => setFilter(filter === 'warn' ? 'all' : 'warn')}
          className={`text-left rounded-xl border-2 p-4 transition ${filter === 'warn' ? 'border-amber-500 bg-amber-900/30' : 'border-amber-700 bg-amber-900/15 hover:bg-amber-900/25'}`}>
          <div className="text-xs uppercase tracking-wider text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Warn</div>
          <div className="text-3xl font-bold text-amber-200 mt-1">{counts.warn}</div>
        </button>
        <button onClick={() => setFilter(filter === 'fail' ? 'all' : 'fail')}
          className={`text-left rounded-xl border-2 p-4 transition ${filter === 'fail' ? 'border-red-500 bg-red-900/30' : 'border-red-700 bg-red-900/15 hover:bg-red-900/25'}`}>
          <div className="text-xs uppercase tracking-wider text-red-300 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Fail</div>
          <div className="text-3xl font-bold text-red-200 mt-1">{counts.fail}</div>
        </button>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
        <div className="flex items-center gap-2 text-xs">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-400">Filter:</span>
          {(['all', 'pass', 'warn', 'fail'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2.5 py-0.5 rounded-full font-medium ${
                filter === f ? 'bg-rb-brand text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}>
              {f.toUpperCase()} ({f === 'all' ? steps.length : counts[f]})
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Sliders className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-400">
              Adjust on Diffuser Module / Module Array — page recomputes live
            </span>
          </div>
        </div>
      </div>

      {/* Step groups */}
      <div className="space-y-3">
        {Array.from(groups.entries()).map(([level, levelSteps]) => (
          <div key={level} className={`rounded-xl border-2 ${LEVEL_COLOURS[level] ?? 'border-slate-700 bg-slate-900'}`}>
            <div className="px-4 py-2 border-b border-slate-700/50 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-100">{level}</div>
              <div className="text-xs text-slate-400">
                {levelSteps.filter(s => s.status === 'pass').length} pass · {levelSteps.filter(s => s.status === 'warn').length} warn · {levelSteps.filter(s => s.status === 'fail').length} fail
              </div>
            </div>
            <div className="divide-y divide-slate-800/50">
              {levelSteps.map(s => (
                <div key={s.id} className="px-4 py-2 flex items-start gap-3 text-xs">
                  <div className="shrink-0 pt-0.5">
                    {s.status === 'pass'
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      : s.status === 'warn'
                      ? <AlertTriangle className="w-4 h-4 text-amber-400" />
                      : <XCircle className="w-4 h-4 text-red-400" />}
                  </div>
                  <div className="font-mono text-slate-500 w-10 shrink-0 pt-0.5">{s.step}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-100 font-medium">{s.param}</div>
                    <div className="text-slate-500 text-[10px] mt-0.5">{s.detail}</div>
                  </div>
                  <div className="text-right shrink-0 w-32">
                    <div className={`font-bold tabular-nums ${
                      s.status === 'pass' ? 'text-emerald-300' :
                      s.status === 'warn' ? 'text-amber-300' : 'text-red-300'
                    }`}>{s.value} {s.unit}</div>
                    <div className="text-slate-500 text-[10px] mt-0.5">{s.target}</div>
                  </div>
                  <div className="text-slate-500 italic text-[10px] w-32 shrink-0 pt-0.5">{s.ref}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-8 text-center text-slate-500 text-sm">
          No checks match the current filter.
        </div>
      )}
    </div>
  );
}
